import { useEffect, useRef } from "react";
import Waveform from "@/components/Waveform";
import { Heart, Droplet, Wind, Gauge, Activity } from "lucide-react";

const fmt = (v, d = 0) => (v == null || v <= 0 ? "--" : d ? Number(v).toFixed(d) : Math.round(v));

function Read({ label, testkey, unit, value, color, alarm, Icon }) {
  return (
    <div className={`bg-[#0a0a0a] border-2 rounded-md px-2.5 py-1.5 ${alarm ? "alarm-border" : "border-[#222]"}`} data-testid={`vm-${testkey}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-clinical font-bold" style={{ color }}>
          <Icon className="w-2.5 h-2.5" />{label}
        </span>
        <span className="text-[8px] text-slate-500 font-mono-clinical">{unit}</span>
      </div>
      <div className={`font-mono-clinical font-bold leading-none ${alarm ? "alarm-pulse" : ""}`} style={{ color, fontSize: "1.55rem" }}>{value}</div>
    </div>
  );
}

function WaveBox({ label, color, kind, rate, amp }) {
  return (
    <div className="bg-black border border-[#222] rounded-md overflow-hidden">
      <div className="px-2 py-0.5 border-b border-[#1a1a1a]">
        <span className="text-[9px] uppercase tracking-widest font-mono-clinical font-bold" style={{ color }}>{label}</span>
      </div>
      <Waveform kind={kind} color={color} height={52} rateRef={rate} amplitudeRef={amp} />
    </div>
  );
}

/* Reusable intra-op / patient monitor. Pass a static `vitals` snapshot per scenario:
   { hr, rr, spo2, map, bp, etco2, temp, alarm }. Zero/absent values flat-line the
   relevant trace (apnoea, arrest, tube obstruction) and show "--". */
export function VitalsMonitor({ vitals = {}, className = "" }) {
  const hr = vitals.hr ?? 0, rr = vitals.rr ?? 0, spo2 = vitals.spo2 ?? 0;
  const map = vitals.map ?? 0, etco2 = vitals.etco2 ?? 0, temp = vitals.temp ?? 0;

  const hrRef = useRef(60), rrRef = useRef(12), ecgAmp = useRef(1), plethAmp = useRef(1), capnoAmp = useRef(1);
  useEffect(() => {
    hrRef.current = hr > 0 ? hr : 60;
    rrRef.current = rr > 0 ? rr : 12;
    ecgAmp.current = hr > 0 ? 1 : 0;
    plethAmp.current = hr <= 0 || spo2 <= 0 ? 0 : spo2 < 90 ? 0.6 : 1;
    capnoAmp.current = rr > 0 ? 1 : 0;
  }, [hr, rr, spo2]);

  return (
    <div className={`rounded-lg border-2 border-[#222] bg-[#050505] p-3 shadow-xl ${className}`} data-testid="vitals-monitor">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${vitals.alarm ? "bg-[#FF3333] alarm-pulse" : "bg-[#39FF14]"}`} />
          <span className="text-[10px] uppercase tracking-widest font-mono-clinical font-bold" style={{ color: vitals.alarm ? "#FF8080" : "#94a3b8" }} data-testid="vm-alarm">
            {vitals.alarm || "Stable"}
          </span>
        </div>
        <span className="text-[10px] text-slate-500 font-mono-clinical hidden sm:inline">PATIENT MONITOR · 25 mm/s</span>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 sm:col-span-7 space-y-1.5">
          <WaveBox label="ECG II" color="#39FF14" kind="ecg" rate={hrRef} amp={ecgAmp} />
          <WaveBox label="SpO₂ Pleth" color="#00FFFF" kind="spo2" rate={hrRef} amp={plethAmp} />
          <WaveBox label="EtCO₂ Capno" color="#FFFFFF" kind="capno" rate={rrRef} amp={capnoAmp} />
        </div>
        <div className="col-span-12 sm:col-span-5 grid grid-cols-2 gap-1.5 content-start">
          <Read label="HR" testkey="hr" unit="bpm" value={fmt(hr)} color="#39FF14" alarm={hr <= 0 || hr < 50 || hr > 180} Icon={Heart} />
          <Read label="SpO₂" testkey="spo2" unit="%" value={fmt(spo2)} color="#00FFFF" alarm={spo2 <= 0 || spo2 < 92} Icon={Droplet} />
          <Read label="NIBP" testkey="nibp" unit="mmHg" value={vitals.bp || fmt(map)} color="#FF8C42" alarm={map > 0 && (map < 60 || map > 120)} Icon={Gauge} />
          <Read label="EtCO₂" testkey="etco2" unit="mmHg" value={fmt(etco2)} color="#FFFFFF" alarm={etco2 <= 0 || etco2 < 30 || etco2 > 55} Icon={Activity} />
          <Read label="RR" testkey="rr" unit="/min" value={fmt(rr)} color="#FFEA00" alarm={rr <= 0 || rr < 6 || rr > 30} Icon={Wind} />
          <Read label="Temp" testkey="temp" unit="°C" value={fmt(temp, 1)} color="#FFFFFF" alarm={temp > 0 && (temp < 36.5 || temp > 39.5)} Icon={Activity} />
        </div>
      </div>
    </div>
  );
}
